import React, { useState } from 'react';

import { Row, Col } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { useStateValue } from '@contexts/StateProvider';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { fetchCustomersByArea } from '@hooks/API/Contact/UseContactsList';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInputNumber } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const ClientAgencyFeeDrawer = ({ networkAgency, customer, onSetCustomer, onClose, onReloadSearch }) => {
    useFormFieldFocus('Commission');
    const [touchForm, setTouchForm] = useState(false);
    const [{ areaContext }] = useStateValue();

    const initialValues = {
        Commission: networkAgency.AgencyFee,
    };

    const saveNetworkAgencyCommission = async (values, setSubmitting) => {
        setSubmitting(true);
        try {
            await Axios.post('/customers/setAgencyFee', {
                ContactID: networkAgency.ContactID,
                NetworkAgencyID: networkAgency.NetworkAgencyID,
                Commission: parseFloat(values.Commission).toFixed(2),
            });
            if (customer.NWAGS && customer.NWAGS.length > 0) {
                const networkAgencyIndex = customer.NWAGS.findIndex((agency) => agency.ID === networkAgency.ID);
                let updatedCustomer = { ...customer };
                updatedCustomer.NWAGS[networkAgencyIndex].AgencyFee = parseFloat(values.Commission).toFixed(2);
                onSetCustomer(updatedCustomer);
                fetchCustomersByArea();
            } else {
                forceReloadContact(customer.ID);
            }
            if (onReloadSearch) {
                onReloadSearch();
            }
            setSubmitting(false);
            onClose();
        } catch (error) {
            console.error('Update Network Agency Fee Error => ', error);
            setSubmitting(false);
        }
    };

    return (
        <NwDrawer
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            title={'Edit Client Default Agency Fee'}
            onClose={onClose}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    Commission: Yup.number()
                        .typeError('Commission must be a number')
                        .required('Agency fee is required, add zero for no commission')
                        .min(0, 'Please input correct percentage, it should be between 0 and 99.99')
                        .max(99.99, 'Please input correct percentage, it should be between 0 and 99.99')
                        .test('max-two-decimals', 'Please input only two decimals', (value) => {
                            if (value === undefined || value === null) {
                                return true;
                            }
                            return /^\d+(\.\d{1,2})?$/.test(String(value));
                        }),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    saveNetworkAgencyCommission(values, setSubmitting);
                }}
            >
                {({ isSubmitting, isValid, values, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            label="Custom Agency Fee Percentage"
                            name="Commission"
                            component={NWInputNumber}
                            style={{ width: '6rem' }}
                            min={0}
                            step={0.1}
                            value={values.Commission}
                        />
                        <NwFormButtonsBar
                            style={{ marginTop: '2rem' }}
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setTouchForm(false);
                                        onClose();
                                    }}
                                />
                            }
                            right={
                                <NwSaveButton
                                    htmlType="submit"
                                    disabled={!isValid || isSubmitting}
                                    loading={isSubmitting}
                                />
                            }
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default ClientAgencyFeeDrawer;
